import { Outlet, useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { LogOut, Menu, X, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from '../contexts/SubscriptionContext';
import { inventoryApi, setApiTenant } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { buildNavGroups } from './nav-groups';
import type { NavGroup } from './nav-groups';
import { AgentChat } from '../components/admin/AgentChat';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { UniversalSearch } from '../components/admin/UniversalSearch';

const IMPERSONATION_STORAGE_KEY = 'ls.super_admin_impersonation';
const DEFAULT_SUPER_ADMIN_URL = 'https://admin.sealedkiss.com/admin';

function sanitizeReturnToUrl(value: string | null): string {
    if (!value) return DEFAULT_SUPER_ADMIN_URL;
    try {
        const parsed = new URL(value, window.location.origin);
        const allowedOrigins = new Set([
            'https://admin.sealedkiss.com',
            'https://admin.lipstick.studio',
            window.location.origin,
        ]);

        if (!allowedOrigins.has(parsed.origin)) {
            return DEFAULT_SUPER_ADMIN_URL;
        }

        return parsed.toString();
    } catch {
        return DEFAULT_SUPER_ADMIN_URL;
    }
}

function CollapsibleNavGroup({
    group,
    isActive,
    isSuperAdmin,
}: {
    group: NavGroup;
    isActive: (path: string) => boolean;
    isSuperAdmin: boolean;
}) {
    const location = useLocation();
    // Auto-expand if any child route is active
    const hasActiveChild = group.items.some((item) => isActive(item.to));
    const [expanded, setExpanded] = useState(!group.collapsible || hasActiveChild);

    // Keep expanded when navigating to a child
    useEffect(() => {
        if (hasActiveChild && !expanded) {
            setExpanded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <div>
            {group.collapsible ? (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="w-full flex items-center justify-between px-3 text-xs font-semibold text-[var(--sidebar-text-muted)] uppercase tracking-wider mb-2 mt-4 first:mt-2 hover:text-[var(--sidebar-text)] transition-colors"
                >
                    <span>{group.section}</span>
                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
            ) : (
                <p className="px-3 text-xs font-semibold text-[var(--sidebar-text-muted)] uppercase tracking-wider mb-2 mt-4 first:mt-2">
                    {group.section}
                </p>
            )}
            {expanded && group.items.map((item) => {
                const badge = 'badge' in item ? item.badge : undefined;
                return item.comingSoon && !isSuperAdmin ? (
                    <button
                        key={item.to}
                        type="button"
                        disabled
                        aria-disabled="true"
                        title="Coming soon"
                        className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-[var(--sidebar-text-muted)] cursor-not-allowed"
                    >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                        <Badge variant="outline" className="ml-auto border-amber-200 bg-amber-50 text-amber-800">
                            Coming soon
                        </Badge>
                        {badge && (
                            <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold">
                                {badge > 99 ? '99+' : badge}
                            </span>
                        )}
                    </button>
                ) : (
                    <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive(item.to)
                            ? 'font-medium text-[var(--sidebar-active-text)]'
                            : 'text-[var(--sidebar-text)] hover:bg-[var(--sidebar-hover-bg)] hover:text-white'
                            }`}
                        style={isActive(item.to) ? { background: 'var(--sidebar-active-bg)', borderLeft: '2px solid var(--sidebar-active-border)', paddingLeft: '10px' } : undefined}
                    >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                        {item.comingSoon && (
                            <Badge variant="outline" className="ml-auto border-amber-200 bg-amber-50 text-amber-800">
                                Coming soon
                            </Badge>
                        )}
                        {badge && (
                            <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold">
                                {badge > 99 ? '99+' : badge}
                            </span>
                        )}
                    </Link>
                );
            })}
        </div>
    );
}

export default function TenantLayout() {
    const { tenantId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { signOut, user, roles: userRoles, tenants, getToken, isLoading: authLoading } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [lowStockCount, setLowStockCount] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);

    // ⌘K / Ctrl+K keyboard shortcut for universal search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen((prev) => !prev);
            }
            if (e.key === 'Escape' && searchOpen) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchOpen]);
    const activeTenant = tenants.find((tenant) => tenant.id === tenantId || tenant.slug === tenantId);
    const isSuperAdmin = !authLoading && userRoles?.includes('super_admin');

    useEffect(() => {
        if (!tenantId) {
            setApiTenant(null);
            return () => setApiTenant(null);
        }

        if (isSuperAdmin || activeTenant) {
            setApiTenant({
                id: activeTenant?.id ?? null,
                slug: activeTenant?.slug ?? tenantId,
            });
        } else {
            // Do not scope requests to arbitrary tenant slugs the user does not belong to.
            setApiTenant(null);
        }

        return () => setApiTenant(null);
    }, [tenantId, isSuperAdmin, activeTenant]);

    // Close sidebar on route change (mobile nav)
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        let isCancelled = false;

        if (!tenantId || (!isSuperAdmin && !activeTenant)) {
            setLowStockCount(0);
            return () => {
                isCancelled = true;
            };
        }

        const fetchLowStockCount = async () => {
            try {
                const response = await inventoryApi.list({ low_stock: true }, getToken);
                if (isCancelled) return;
                const count = Array.isArray(response?.items) ? response.items.length : 0;
                setLowStockCount(count);
            } catch (error) {
                console.error('Failed to load low stock count:', error);
                if (!isCancelled) setLowStockCount(0);
            }
        };

        fetchLowStockCount();

        return () => {
            isCancelled = true;
        };
    }, [getToken, tenantId, location.pathname, isSuperAdmin, activeTenant]);

    const [impersonationReturnTo, setImpersonationReturnTo] = useState<string | null>(null);
    const [isImpersonating, setIsImpersonating] = useState(false);

    const impersonation = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const impersonate = params.get('impersonate');
        const returnTo = params.get('returnTo');
        return { impersonate, returnTo };
    }, [location.search]);

    useEffect(() => {
        // Start impersonation mode if query param is present.
        if (impersonation.impersonate) {
            setIsImpersonating(true);
            const returnTo = sanitizeReturnToUrl(impersonation.returnTo);
            setImpersonationReturnTo(returnTo);
            try {
                localStorage.setItem(
                    IMPERSONATION_STORAGE_KEY,
                    JSON.stringify({ tenantSlug: tenantId, returnTo, startedAt: new Date().toISOString() })
                );
            } catch {
                // Ignore storage errors.
            }
            return;
        }

        // Restore impersonation mode from storage (e.g. hard refresh).
        try {
            const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as { tenantSlug?: string; returnTo?: string };
            if (!parsed?.tenantSlug || parsed.tenantSlug !== tenantId) return;
            setIsImpersonating(true);
            setImpersonationReturnTo(sanitizeReturnToUrl(parsed.returnTo || null));
        } catch {
            // Ignore.
        }
    }, [impersonation.impersonate, impersonation.returnTo, tenantId]);

    const stopImpersonation = () => {
        setIsImpersonating(false);
        setImpersonationReturnTo(null);
        try {
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        } catch {
            // Ignore.
        }
    };

    const visibleNavLinks = buildNavGroups(tenantId!, isSuperAdmin, lowStockCount);
    const isActive = (path: string) => location.pathname === path;

    return (
        <SubscriptionProvider tenantId={tenantId!}>
        <div className="min-h-screen" style={{ backgroundColor: 'var(--color-surface-base)' }}>
            {/* Mobile header */}
            <header className="lg:hidden fixed top-0 inset-x-0 z-30 border-b border-[var(--sidebar-border)] flex items-center justify-between px-4 h-14" style={{ background: 'var(--sidebar-bg)' }}>
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="p-2 -ml-2 text-[var(--sidebar-text)] hover:text-white rounded-lg hover:bg-[var(--sidebar-hover-bg)] transition-colors"
                    aria-label="Open menu"
                >
                    <Menu size={22} />
                </button>
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #d46f84 0%, #b9576d 100%)' }}>
                        SW
                    </div>
                    <span className="font-bold text-white text-sm">SWAK</span>
                </div>
                <button
                    onClick={() => setSearchOpen(true)}
                    className="p-2 -mr-2 text-[var(--sidebar-text)] hover:text-white rounded-lg hover:bg-[var(--sidebar-hover-bg)] transition-colors"
                    aria-label="Search"
                >
                    <Search size={20} />
                </button>
            </header>

            {/* Backdrop overlay (mobile) */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/40 transition-opacity"
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed inset-y-0 left-0 z-50 w-64 border-r border-[var(--sidebar-border)] flex flex-col font-sans overflow-hidden
                    transition-transform duration-200 ease-in-out
                    lg:translate-x-0 lg:z-10
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                style={{ background: 'var(--sidebar-bg)' }}
            >
                {/* Subtle ambient glow */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
                    <div style={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-30%',
                        width: '60%',
                        height: '50%',
                        background: 'radial-gradient(ellipse, rgba(212,111,132,0.07) 0%, transparent 70%)',
                    }} />
                </div>
                <div className="p-6 border-b border-[var(--sidebar-border)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(135deg, #d46f84 0%, #b9576d 100%)' }}>
                            SW
                        </div>
                        <div className="min-w-0">
                            <h1 className="font-bold text-white leading-tight">SWAK</h1>
                            {!isSuperAdmin && (
                                <p className="text-xs text-[var(--sidebar-text-muted)] truncate">{activeTenant?.name || tenantId}</p>
                            )}
                            {isSuperAdmin && (
                                <div className="mt-1">
                                    <label htmlFor="tenant-switcher" className="sr-only">Select tenant</label>
                                    <select
                                        id="tenant-switcher"
                                        value={tenantId}
                                        onChange={(e) => {
                                            const slug = e.target.value;
                                            navigate(`/${slug}/dashboard`);
                                        }}
                                        className="w-full text-xs bg-[var(--sidebar-bg-elevated)] border border-[var(--sidebar-border)] rounded-md px-2 py-1 text-[var(--sidebar-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sidebar-active-border)]"
                                    >
                                        {(tenants || []).map((t) => (
                                            <option key={t.slug} value={t.slug}>{t.name || t.slug}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-1.5 text-[var(--sidebar-text-muted)] hover:text-white rounded-lg hover:bg-[var(--sidebar-hover-bg)] transition-colors"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="relative flex-1 p-4 space-y-1 overflow-y-auto">
                    {/* Universal Search trigger */}
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 mb-2 rounded-lg text-[var(--sidebar-text-muted)] hover:bg-[var(--sidebar-hover-bg)] hover:text-white transition-colors border border-[var(--sidebar-border)]"
                    >
                        <Search size={16} />
                        <span className="flex-1 text-left text-sm">Search…</span>
                        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-[var(--sidebar-bg-elevated)] border border-[var(--sidebar-border)] rounded text-[var(--sidebar-text-muted)]">
                            ⌘K
                        </kbd>
                    </button>

                    {visibleNavLinks.map((group) => (
                        <CollapsibleNavGroup
                            key={group.section}
                            group={group}
                            isActive={isActive}
                            isSuperAdmin={isSuperAdmin}
                        />
                    ))}
                </nav>

                <TrialBanner tenantId={tenantId!} />

                <div className="p-4 border-t border-[var(--sidebar-border)]">
                    <div className="flex items-center gap-3 px-3 py-2 mb-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold border border-[var(--sidebar-border)]" style={{ background: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-text)' }}>
                            {user?.name?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                            <p className="text-xs text-[var(--sidebar-text-muted)] truncate">{user?.email}</p>
                        </div>
                    </div>
                    <div className="px-1 mb-2">
                        <ThemeSwitcher variant="inline" />
                    </div>
                    <button
                        onClick={signOut}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-rose-400/80 hover:bg-[var(--sidebar-hover-bg)] hover:text-rose-300 rounded-lg transition-colors"
                    >
                        <LogOut size={16} />
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* AI Assistant floating chat */}
            <AgentChat />

            {/* Universal Search Command Palette (⌘K) */}
            {searchOpen && (
                <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setSearchOpen(false)}
                    />
                    <div className="relative w-full max-w-xl mx-4 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
                        <UniversalSearch
                            className="w-full"
                            placeholder="Search clients, products, orders, events…"
                            onSelect={() => setSearchOpen(false)}
                        />
                        <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
                            <span>Type to search across all data</span>
                            <span>
                                <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">ESC</kbd> to close
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen overflow-x-hidden">
                {/* Sticky topbar with universal search */}
                <div className="sticky top-14 lg:top-0 z-20 border-b border-[var(--sidebar-border)] px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3" style={{ background: 'var(--color-surface-base)', backdropFilter: 'blur(12px)' }}>
                    <div className="flex-1 max-w-xl">
                        <div data-search-wrapper>
                            <UniversalSearch
                                placeholder="Search products, clients, orders, events… (⌘K)"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                <div className="w-full p-4 sm:p-6 lg:p-8">
                    {isImpersonating && (
                        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <p className="text-sm font-semibold text-amber-900">Impersonation mode</p>
                                <p className="text-sm text-amber-800">
                                    You opened this tenant from <span className="font-medium">Super Admin</span>. Actions here affect tenant <span className="font-medium">{tenantId}</span>.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <a
                                    href={impersonationReturnTo || DEFAULT_SUPER_ADMIN_URL}
                                    className="inline-flex items-center justify-center rounded-lg bg-amber-600 text-white px-3 py-2 text-sm hover:bg-amber-700 transition-colors"
                                    title="Return to Super Admin"
                                >
                                    Return to Super Admin
                                </a>
                                <button
                                    onClick={stopImpersonation}
                                    className="inline-flex items-center justify-center rounded-lg bg-white border border-amber-200 text-amber-900 px-3 py-2 text-sm hover:bg-amber-100 transition-colors"
                                    title="Dismiss banner"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}
                    <Outlet />
                </div>
            </main>
        </div>
        </SubscriptionProvider>
    );
}

function TrialBanner({ tenantId }: { tenantId: string }) {
    const { trial, isLoading } = useSubscription();

    if (isLoading || !trial.isOnTrial) return null;

    const days = trial.daysRemaining;
    const isUrgent = days <= 3;
    const isWarning = days <= 7;

    const bg = isUrgent
        ? 'bg-red-900/30 border-red-700/50'
        : isWarning
        ? 'bg-amber-900/30 border-amber-700/50'
        : 'bg-[var(--sidebar-bg-elevated)] border-[var(--sidebar-border)]';

    const textColor = isUrgent ? 'text-red-300' : isWarning ? 'text-amber-300' : 'text-[var(--sidebar-text)]';
    const subColor = isUrgent ? 'text-red-400/70' : isWarning ? 'text-amber-400/70' : 'text-[var(--sidebar-text-muted)]';

    return (
        <div className={`mx-3 mb-3 rounded-xl border p-3 ${bg}`}>
            <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold uppercase tracking-wider ${textColor}`}>
                    Free Trial
                </span>
                <span className={`text-xs font-bold ${textColor}`}>
                    {days}d left
                </span>
            </div>
            <div className="w-full bg-black/20 rounded-full h-1.5 mb-2">
                <div
                    className={`h-1.5 rounded-full transition-all ${isUrgent ? 'bg-red-400' : isWarning ? 'bg-amber-400' : 'bg-[var(--sidebar-active-border)]'}`}
                    style={{ width: `${Math.min(100, (days / 14) * 100)}%` }}
                />
            </div>
            <p className={`text-xs ${subColor} mb-2`}>
                All features unlocked during trial.
            </p>
            <Link
                to={`/${tenantId}/settings/billing`}
                className="block w-full text-center text-xs font-medium py-1.5 rounded-lg bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-text)] hover:opacity-90 transition-opacity"
            >
                Upgrade plan
            </Link>
        </div>
    );
}
